'use client';

import {
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslations } from 'next-intl';
// Direct module imports, not the `components/landing` barrel: the barrel also
// pulls the carousels/banner-grid this page no longer renders.
import HeroCarousel from 'components/landing/HeroCarousel';
import { CampaignHeroBanner } from 'components/landing/CampaignHeroBanner';
import { RingProHero } from 'components/landing/RingProHero';
import { TwoHourDeliveryHero } from 'components/landing/TwoHourDeliveryHero';
import { BiomarkersScienceSection } from 'components/landing/v3/BiomarkersScienceSection';
import { BatteryLifeSection } from 'components/landing/v3/BatteryLifeSection';
import { EmeraldArcSection } from 'components/landing/v3/EmeraldArcSection';
import { FormFactorsSection as TwoFormFactorsSection } from 'components/landing/v3/formFactors';
import { HomepageLenis } from 'components/landing/v3/HomepageLenis';
import { OverlapRevealStack } from 'components/landing/v3/OverlapRevealStack';
import { PartnerLogoMarquee } from 'components/landing/v3/PartnerLogoMarquee';
import { RingMeasuresSection } from 'components/landing/v3/RingMeasuresSection';
import { JsonLd } from 'components/shared';
import TypekitFonts from 'components/shared/TypekitFonts';
import { useCartStore } from 'components/shared/Cart';
import { TierCampaignChip } from 'components/ring-pro/TierCampaignChip';
import {
  fetchTierCampaignData,
  getRingProBasePrice,
} from 'content/products/ring-pro/tierPricing';
import type { TierCampaignData } from 'components/ring-pro/utils/types';
import { Currency } from 'utils/Currency';
import { CurrencySymbol } from 'components/shared/CurrencySymbol';
import { isMCFCountry } from 'lib/region/countryUtils';
import { m1PromoDiscount } from 'content/referral';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { generateBreadcrumbSchema } from 'lib/seo/schema';
import { getBaseStore, ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { jetbrainsMono, geist } from 'utils/fonts';
import { TWO_HOUR_HOMEPAGE_HERO_ENABLED } from 'utils/featureFlags';
import styles from './home-client.module.css';

/**
 * Homepage v3 (main `854ca38b2` "the new face of Ultrahuman"). The banner /
 * carousel stack below the hero was retired in favour of six scroll-driven
 * sections; `components/landing` keeps the retired components for other routes.
 *
 * The Ring PRO hero is main's `components/landing/RingProHero` — a full-bleed
 * video card that replaced the v2 `Banner` composition, so `Banner` is no
 * longer rendered here.
 */
const HomePage = ({ worldSleep }: { worldSleep?: ReactNode }) => {
  const {
    region: country,
    store: regionStore,
    currency: regionCurrency,
  } = useRegionLocale();
  const upperCountry = (country || '').toUpperCase();

  const [tierCampaign, setTierCampaign] = useState<TierCampaignData | null>(
    null,
  );
  useEffect(() => {
    fetchTierCampaignData().then((data) => {
      if (data?.campaign_active) setTierCampaign(data);
    });
  }, []);

  const store = regionStore ?? ShopifyStore.ROW;
  const ringProCurrency = (regionCurrency as Currency) || Currency.usd;
  const ringProBasePrice =
    getRingProBasePrice(store as ShopifyStore, ringProCurrency) ?? 0;
  const ringProCurrencySymbol = CurrencySymbol[ringProCurrency] ?? '$';

  const campaigns = useCartStore((s) => s.campaigns);
  const t = useTranslations('homepage');

  const tierChipNode = useMemo(
    () =>
      tierCampaign && ringProBasePrice > 0 ? (
        <TierCampaignChip
          tierCampaign={tierCampaign}
          basePrice={ringProBasePrice}
          currencySymbol={ringProCurrencySymbol}
          store={store as string}
        />
      ) : undefined,
    [tierCampaign, ringProBasePrice, ringProCurrencySymbol, store],
  );

  const heroCarouselItems = useMemo(() => {
    const campaignItems = campaigns
      .filter((campaign) => campaign.campaign_banner_component)
      .sort((a, b) => {
        if (a.product === 'ring' && b.product !== 'ring') return -1;
        if (a.product !== 'ring' && b.product === 'ring') return 1;
        return 0;
      })
      .map((campaign) => ({
        component: ({ eager }: { eager?: boolean }) => (
          <CampaignHeroBanner
            campaign_banner_component={campaign.campaign_banner_component!}
            product={campaign.product}
            eager={eager}
          />
        ),
        handleTheme: 'light' as const,
        handleFill: true,
        activeCountries:
          campaign.country?.toUpperCase() === 'ROW'
            ? undefined
            : campaign.country
              ? [campaign.country.toUpperCase()]
              : undefined,
      }));

    if (!country || country === 'AU') return campaignItems;

    const resolvedStore = regionStore ?? ShopifyStore.ROW;
    const baseStore = getBaseStore(resolvedStore);
    const m1Discount = m1PromoDiscount[baseStore];
    const items = [...campaignItems];

    const shouldShowM1Campaign =
      m1Discount &&
      m1Discount.active &&
      baseStore &&
      (baseStore !== ShopifyStore.EU || isMCFCountry(country));

    if (shouldShowM1Campaign) {
      const bannerComponent = {
        variant: 'm1',
        title:
          resolvedStore === ShopifyStore.AE
            ? t('homepage.m1Campaign.whiteFridaySale')
            : t('homepage.m1Campaign.blackFridaySale'),
        description: t('homepage.m1Campaign.saveOnM1', {
          discount: m1Discount.discount,
        }),
        cta_text: t('homepage.cta.buyNow'),
        cta_link: '/pricing/',
        desktop_background_image_url: 'web_v2/campaigns/bfs_m1_25_updated.png',
        mobile_background_image_url:
          'web_v2/campaigns/bfs_m1_25_resp_updated.png',
      };

      items.push({
        component: ({ eager }: { eager?: boolean }) => (
          <CampaignHeroBanner
            campaign_banner_component={bannerComponent}
            product="m1"
            eager={eager}
          />
        ),
        handleTheme: 'light' as const,
        handleFill: true,
        activeCountries: [upperCountry],
      });
    }

    return items;
  }, [campaigns, country, regionStore, t, upperCountry]);

  const homepageSchemas = [
    generateBreadcrumbSchema([
      {
        name: t('homepage.breadcrumb.home'),
        url: 'https://www.ultrahuman.com',
      },
    ]),
  ];

  const showTwoHourHero =
    TWO_HOUR_HOMEPAGE_HERO_ENABLED && upperCountry === 'IN';

  return (
    <main
      id="main-content"
      role="main"
      className={`${styles.pageMain} ${jetbrainsMono.variable} ${geist.variable}`}
      aria-label={t('homepage.aria.pageMain')}
    >
      <h1 className={styles.srOnly}>{t('homepage.srOnly.heading')}</h1>
      <JsonLd data={homepageSchemas} />
      <HomepageLenis />
      <TypekitFonts kits={['futura', 'dharma']} />
      {heroCarouselItems.length ? (
        <HeroCarousel
          ariaLabel={t('homepage.aria.heroCarousel')}
          items={heroCarouselItems}
          autoplayDelay={10000}
          stopOnInteraction={true}
          autoplayStopAtEnd={true} // FIXME: this is not working
        />
      ) : null}
      {showTwoHourHero ? (
        <TwoHourDeliveryHero
          stackedBelowCarousel={heroCarouselItems.length > 0}
        />
      ) : null}
      <RingProHero
        note={tierChipNode}
        primaryButtonType="ring_pro_banner_buy_button"
        aria-label={t('homepage.aria.ringProSection')}
        hasCampaignAbove={heroCarouselItems.length > 0 || showTwoHourHero}
      />
      <BiomarkersScienceSection
        aria-label={t('homepage.biomarkersScience.aria.section')}
      />
      <BatteryLifeSection aria-label={t('homepage.batteryLife.aria.section')} />
      <OverlapRevealStack>
        <RingMeasuresSection
          aria-label={t('homepage.ringMeasures.aria.section')}
        />
        <TwoFormFactorsSection
          aria-label={t('homepage.twoFormFactors.aria.section')}
        />
      </OverlapRevealStack>
      <EmeraldArcSection
        aria-label={t('homepage.emerald.aria.updateSection')}
      />
      {worldSleep}
      <PartnerLogoMarquee
        aria-label={t('homepage.scienceLibrary.aria.partners')}
      />
    </main>
  );
};

export default HomePage;
